import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { EMPTY_LIST, NOTIFICATION_TYPE } from '../../../../constants/common'
import { useMassActions, useSelection } from '../../../../hooks'
import { useAsyncTable } from '../../../Table'
import { getMassActionsSuccessItemsLength } from '../../DocFlow.utils'
import { setStorageCombiningPhaseAsyncStatus } from '../../DocFlow.utils.storage'
import { DOC_FLOW_ADD_RECIPIENT_FIELDS_FORM_FIELD_RECIPIENT } from '../../DocFlowAddRecipientFields'
import {
  addNewCombining,
  checkCombiningTask,
  checkExistNotFoundUsers,
  DOC_FLOW_COMBINING_ACTION_ADD_RECIPIENT,
  DOC_FLOW_COMBINING_ADD_RECIPIENT_INITIAL_VALUIES,
  getAsyncNotificationText,
  getMassActions,
  getNotificationTitle,
  getParamsForAddingRecipients,
  getTableApiUrls,
  isDocFlowCombiningPhaseLoading,
} from '../../DocFlowCombining'
import {
  DocFlowRecipientModal,
  getValidationSchemaDocFlowRecipientModalMultiField,
} from '../../DocFlowRecipientModal'
import { useCombiningTableActions } from '../useCombiningTableActions'
import { useDocFlowContext } from '../useDocFlowContext'
import { useLoadCombiningPhaseAsync } from '../useLoadCombiningPhaseAsync'

export function useCombiningPhase() {
  const {
    asyncActionInProccess,
    docFlow,
    documentId,
    handleCloseModal,
    handleReloadDocFlow,
    setFooterProps,
    setModal,
  } = useDocFlowContext()

  const { t } = useTranslation()

  const tableData = useAsyncTable({
    ...getTableApiUrls(documentId),
    withoutSubMenu: true,
  })

  const {
    filterValues,
    handleLoadInit,
    handleReload,
    isInitiated,
    items: tableItems,
    pagination,
  } = tableData

  const selectionData = useSelection({ data: tableItems, pagination })

  // Declared before `useMassActions` (which consumes it as `onSuccessHandler`)
  // so the React Compiler memoizes it — a function declaration referenced
  // before its lexical position is left un-memoized and would churn every
  // render, destabilizing the whole footer-props chain.
  function handleSuccesAsyncAction() {
    setStorageCombiningPhaseAsyncStatus(false)

    const handler = isInitiated ? handleReload : handleLoadInit

    handler().then(handleReloadDocFlow)
  }

  const {
    getAsyncTaskId,
    handleAddAsyncMassActionNotification,
    handleAddFinishMassActionNotification,
  } = useMassActions({
    getAsyncNotificationText,
    getMassSuccessItemsLength: getMassActionsSuccessItemsLength,
    getNotificationTitle,
    onSuccessHandler: handleSuccesAsyncAction,
  })

  const docFlowAsyncTask = getAsyncTaskId(docFlow)

  useLoadCombiningPhaseAsync({
    asyncTask: docFlowAsyncTask,
    onLoadInit: handleLoadInit,
  })

  function handleAsyncAction({ action, task = docFlowAsyncTask, totalCount }) {
    setStorageCombiningPhaseAsyncStatus(true)

    handleAddAsyncMassActionNotification({
      action,
      checkTaskFetch: checkCombiningTask,
      linkProps: {
        title: t('Notification.CommonAsyncActionFinishedOpenResultKey'),
      },
      task,
      totalCount,
    })
  }

  const {
    handleCombinedDocument,
    isPerformingMassAction,
    setItemAction,
    setMassAction,
  } = useCombiningTableActions({
    filterValues,
    getAsyncTaskId,
    pagination,
    selectionData,
    tableItems,
    onAddFinishMassActionNotification: handleAddFinishMassActionNotification,
    onAsyncAction: handleAsyncAction,
    onReloadTable: handleReload,
  })

  function handleAddRecipient({ value }) {
    addNewCombining(getParamsForAddingRecipients({ documentId, value }))
      .then(({ data }) => {
        handleAsyncAction({
          action: DOC_FLOW_COMBINING_ACTION_ADD_RECIPIENT,
          task: getAsyncTaskId(data),
          totalCount:
            value[DOC_FLOW_ADD_RECIPIENT_FIELDS_FORM_FIELD_RECIPIENT].length,
        })
      })
      .finally(() => {
        handleCloseModal()
      })
  }

  function handleClickAddRecipient() {
    return setModal(
      <DocFlowRecipientModal
        documentId={documentId}
        initialValues={DOC_FLOW_COMBINING_ADD_RECIPIENT_INITIAL_VALUIES}
        validationSchema={getValidationSchemaDocFlowRecipientModalMultiField()}
        onCloseModal={handleCloseModal}
        onSubmit={handleAddRecipient}
      />
    )
  }

  const nextPhaseNotifications = checkExistNotFoundUsers(tableItems?.items)
    ? [
        {
          text: t('DocumentStapling.CombiningNextPhaseNotificationpKey'),
          type: NOTIFICATION_TYPE.warning,
        },
      ]
    : EMPTY_LIST

  const headerActionsProps = {
    addRecipientDisabled: asyncActionInProccess,
    notifications: nextPhaseNotifications,
    onClickAddRecipientBtn: handleClickAddRecipient,
  }

  useEffect(() => {
    setFooterProps(headerActionsProps)
  }, [headerActionsProps])

  useEffect(() => {
    if (isInitiated) {
      handleReload()
    }
  }, [docFlow?.updated])

  function getCombiningMassActions() {
    return getMassActions(docFlow)
  }

  const loadingPercent = docFlow?.transition_to_combining_progress

  const isPhaseLoading = isDocFlowCombiningPhaseLoading(loadingPercent)

  return {
    asyncActionInProccess,
    getCombiningMassActions,
    handleCombinedDocument,
    isPerformingMassAction,
    isPhaseLoading,
    loadingPercent,
    selectionData,
    setItemAction,
    setMassAction,
    tableData,
  }
}
